/** Analytics slice — the reference anModel() (design-reference/PointFive OS Original.dc.html, script lines 2068–2646),
 *  ported verbatim: KPI band, Overview / Performance / Calendar / Sessions / Trade Log / Day & Time / Strategies /
 *  Technicals / Behaviour tabs, range / strategy / symbol filters, heat maps, scatters, buckets and the trade log.
 *  Everything is derived from the shared ledger (`c.trades()`) plus `c.data.analytics`. */
import type { Ctx } from './ctx';
import type { ModelState } from './state';
import type { ViewModel } from '@/screens/generated/view-model';

interface Trade {
  id: number;
  off: number;
  acct: string;
  sym: string;
  side: string;
  lots: number;
  entry: string;
  exit: string;
  pnl: number;
  durS: number;
  strat: string;
  time: string;
}
interface Agg {
  n: number;
  pnl: number;
  w: number;
}
interface GroupRow {
  label: string;
  n: number;
  pnlRaw: number;
  count: string;
  wr: string;
  pnl: string;
  pnlFg: string;
  w: string;
  bar: string;
}
interface GaugeTick {
  x1: string;
  y1: string;
  x2: string;
  y2: string;
  col: string;
  w: string;
  op: string;
}
interface DdEpisode {
  peakOff: number;
  troughOff: number;
  depth: number;
  recovered: boolean;
  endOff: number | null;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export function buildAnalytics(c: Ctx) {
  const S = c.S;
  const D = c.data.analytics;
  const sgn = (v: number) =>
    (v >= 0 ? '+' : '−') +
    '$' +
    Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const RANGE: Record<string, number> = { week: 6, month: 30, quarter: 90, year: 365, all: 9999 };
  const maxOff = RANGE[S.anRange] as number;
  const all: Trade[] = c.trades().map((r) => ({
    id: r[0],
    off: r[1],
    acct: r[2],
    sym: r[3],
    side: r[4],
    lots: r[5],
    entry: r[6],
    exit: r[7],
    pnl: r[8],
    durS: r[9],
    strat: r[10],
    time: r[12],
  }));
  const T = all.filter(
    (t) =>
      t.off <= maxOff &&
      (S.anStrat === 'all' || t.strat === S.anStrat) &&
      (S.anSym === 'all' || t.sym === S.anSym),
  );

  const wins = T.filter((t) => t.pnl > 0),
    losses = T.filter((t) => t.pnl < 0);
  const net = T.reduce((a, t) => a + t.pnl, 0);
  const grossWin = wins.reduce((a, t) => a + t.pnl, 0);
  const grossLoss = Math.abs(losses.reduce((a, t) => a + t.pnl, 0));
  const winRate = T.length ? (wins.length / T.length) * 100 : 0;
  const pf = grossLoss ? grossWin / grossLoss : 0;
  const avgWin = wins.length ? grossWin / wins.length : 0;
  const avgLoss = losses.length ? grossLoss / losses.length : 0;
  const ratio = avgLoss ? avgWin / avgLoss : 0;
  const expectancy = T.length ? net / T.length : 0;

  // day buckets
  const byDay: Record<number, number> = {};
  T.forEach((t) => {
    byDay[t.off] = (byDay[t.off] || 0) + t.pnl;
  });
  const dayPnl = (k: number) => byDay[k] || 0;
  const dayKeys = Object.keys(byDay)
    .map(Number)
    .sort((a, b) => b - a);
  const winDays = dayKeys.filter((k) => dayPnl(k) > 0).length;
  const loseDays = dayKeys.filter((k) => dayPnl(k) < 0).length;
  const dayWin = dayKeys.length ? (winDays / dayKeys.length) * 100 : 0;

  // streaks
  const ordered = T.slice().sort((a, b) => b.off - a.off);
  let ws = 0,
    bestWs = 0,
    ls = 0,
    worstLs = 0;
  ordered.forEach((t) => {
    if (t.pnl > 0) {
      ws++;
      ls = 0;
      bestWs = Math.max(bestWs, ws);
    } else if (t.pnl < 0) {
      ls++;
      ws = 0;
      worstLs = Math.max(worstLs, ls);
    }
  });

  // cumulative
  let run = 0;
  const cum = dayKeys
    .slice()
    .sort((a, b) => b - a)
    .map((k) => {
      run += dayPnl(k);
      return { off: k, run };
    });
  const cLo = Math.min(0, ...cum.map((p) => p.run)),
    cHi = Math.max(1, ...cum.map((p) => p.run));
  const cPath =
    cum.length > 1
      ? 'M' +
        cum
          .map(
            (p, i) =>
              ((i / (cum.length - 1)) * 420).toFixed(1) +
              ',' +
              (150 - ((p.run - cLo) / (cHi - cLo)) * 138).toFixed(1),
          )
          .join(' L')
      : '';
  const zeroY = (150 - ((0 - cLo) / (cHi - cLo)) * 138).toFixed(1);

  const dayLabelPre = (off: number) => {
    const d = new Date(Date.UTC(2026, 6, 24) - off * 86400000);
    return MONTHS[d.getUTCMonth()] + ' ' + d.getUTCDate();
  };
  const ddEpisodesRaw: DdEpisode[] = (() => {
    const seq = dayKeys.slice().sort((a, b) => b - a);
    if (!seq.length) return [];
    let run = 0,
      peak = 0,
      peakOff = seq[0] as number;
    let inDD = false,
      epStart = 0,
      epPeakVal = 0,
      epTroughVal = 0,
      epTroughOff = 0;
    const episodes: DdEpisode[] = [];
    seq.forEach((off) => {
      run += dayPnl(off);
      if (run >= peak) {
        if (inDD)
          episodes.push({
            peakOff: epStart,
            troughOff: epTroughOff,
            depth: epPeakVal - epTroughVal,
            recovered: true,
            endOff: off,
          });
        inDD = false;
        peak = run;
        peakOff = off;
      } else {
        if (!inDD) {
          inDD = true;
          epStart = peakOff;
          epPeakVal = peak;
          epTroughVal = run;
          epTroughOff = off;
        } else if (peak - run > epPeakVal - epTroughVal) {
          epTroughVal = run;
          epTroughOff = off;
        }
      }
    });
    if (inDD)
      episodes.push({
        peakOff: epStart,
        troughOff: epTroughOff,
        depth: epPeakVal - epTroughVal,
        recovered: false,
        endOff: null,
      });
    return episodes
      .filter((e) => e.depth > 0)
      .sort((a, b) => b.depth - a.depth)
      .slice(0, 4);
  })();

  const dayLabel = (off: number) => {
    const d = new Date(Date.UTC(2026, 6, 24) - off * 86400000);
    return MONTHS[d.getUTCMonth()] + ' ' + d.getUTCDate();
  };
  const weekday = (off: number): string => {
    const d = new Date(Date.UTC(2026, 6, 24) - off * 86400000);
    return WDAYS[d.getUTCDay()] as string;
  };

  const group = (keyFn: (t: Trade) => string): GroupRow[] => {
    const m: Record<string, Agg> = {};
    T.forEach((t) => {
      const k = keyFn(t);
      const e = (m[k] = m[k] || { n: 0, pnl: 0, w: 0 });
      e.n++;
      e.pnl += t.pnl;
      if (t.pnl > 0) e.w++;
    });
    const peak = Math.max(1, ...Object.keys(m).map((k) => Math.abs((m[k] as Agg).pnl)));
    return Object.keys(m)
      .map((k) => {
        const e = m[k] as Agg;
        return {
          label: k,
          n: e.n,
          pnlRaw: e.pnl,
          count: e.n === 1 ? '1 trade' : e.n + ' trades',
          wr: ((e.w / e.n) * 100).toFixed(0) + '%',
          pnl: sgn(e.pnl),
          pnlFg: e.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
          w: ((Math.abs(e.pnl) / peak) * 100).toFixed(1) + '%',
          bar: e.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
        };
      })
      .sort((a, b) => b.pnlRaw - a.pnlRaw);
  };
  // London-time entry hour decides the session — Asia 00:00–06:59,
  // London 07:00–12:59, London/NY overlap 13:00–16:29, NY 16:30+
  const sessionOf = (t: Trade): string => {
    const hh = parseInt(String(t.time).slice(0, 2), 10);
    const mm = parseInt(String(t.time).slice(3, 5), 10);
    if (hh < 7) return 'Asia';
    if (hh < 13) return 'London';
    if (hh < 16 || (hh === 16 && mm < 30)) return 'London → NY overlap';
    return 'New York';
  };

  const gaugeTicks = (pct: number, tone: string): GaugeTick[] => {
    const N = 28,
      lit = (Math.max(0, Math.min(pct, 100)) / 100) * N,
      out: GaugeTick[] = [];
    for (let i = 0; i < N; i++) {
      const a = Math.PI * (1 - (i + 0.5) / N);
      const on = i < lit,
        isCur = on && i + 1 >= lit;
      const ro = isCur ? 100 : on ? 96 : 92,
        ri = isCur ? 70 : on ? 74 : 78;
      out.push({
        x1: (100 + Math.cos(a) * ri).toFixed(1),
        y1: (104 - Math.sin(a) * ri).toFixed(1),
        x2: (100 + Math.cos(a) * ro).toFixed(1),
        y2: (104 - Math.sin(a) * ro).toFixed(1),
        col: on ? tone : 'var(--l10)',
        w: isCur ? '3.2' : on ? '2.6' : '2.2',
        op: on ? (isCur ? '1' : '0.85') : '1',
      });
    }
    return out;
  };

  const TABS: [string, string][] = [
    ['overview', 'Overview'],
    ['performance', 'Performance'],
    ['calendar', 'Calendar'],
    ['sessions', 'Sessions'],
    ['trades', 'Trade Log'],
    ['dayTime', 'Day & Time'],
    ['strategies', 'Strategies'],
    ['technicals', 'Technicals'],
    ['behaviour', 'Behaviour'],
  ];
  // (the reference also builds anStratChips / anSymChips filter chips here; the template never binds them — omitted)

  // calendar — 4 weeks
  const cal: { day: string; val: string; bg: string; fg: string; dayFg: string }[] = [];
  for (let off = 27; off >= 0; off--) {
    const v = byDay[off];
    const peak = Math.max(1, ...Object.keys(byDay).map((k) => Math.abs(dayPnl(Number(k)))));
    const inten = v ? Math.min(Math.abs(v) / peak, 1) : 0;
    cal.push({
      day: String(new Date(Date.UTC(2026, 6, 24) - off * 86400000).getUTCDate()),
      val: v ? sgn(v) : '',
      // tint caps at 26% so the day number and figure stay above AA (same as the Journal)
      bg: !v
        ? 'var(--l4)'
        : v > 0
          ? 'color-mix(in srgb, var(--pos) ' + Math.round(8 + inten * 18) + '%, transparent)'
          : 'color-mix(in srgb, var(--neg) ' + Math.round(8 + inten * 18) + '%, transparent)',
      fg: v ? (v > 0 ? 'var(--pos-2)' : 'var(--neg)') : 'var(--faint)',
      dayFg: v ? 'var(--text-mid)' : 'var(--muted2)',
    });
  }

  const hh2 = (t: Trade) => parseInt(String(t.time).slice(0, 2), 10);
  const mm2 = (t: Trade) => parseInt(String(t.time).slice(3, 5), 10);
  const heatWindow = ({ month: 30, quarter: 90, year: 365, all: 9999 } as Record<string, number>)[
    S.anHeatRange
  ] as number;

  return {
    anTabs: TABS.map(([id, label]) => ({
      label,
      go: () => c.set({ anTab: id }),
      bg: S.anTab === id ? 'var(--rail)' : 'transparent',
      fg: S.anTab === id ? 'var(--rail-ink)' : 'var(--muted)',
      fw: S.anTab === id ? '600' : '500',
    })),
    anIsOverview: S.anTab === 'overview',
    anIsPerf: S.anTab === 'performance',
    anIsCal: S.anTab === 'calendar',
    anIsSessions: S.anTab === 'sessions',
    anIsTrades: S.anTab === 'trades',
    anIsDayTime: S.anTab === 'dayTime',
    anIsStrats: S.anTab === 'strategies',
    anRanges: (
      [
        ['week', 'Week'],
        ['month', 'Month'],
        ['quarter', 'Quarter'],
        ['year', 'Year'],
        ['all', 'All Time'],
      ] as [string, string][]
    ).map(([id, label]) => ({
      label,
      go: () => c.set({ anRange: id }),
      bg: S.anRange === id ? 'var(--rail)' : 'transparent',
      fg: S.anRange === id ? 'var(--rail-ink)' : 'var(--muted)',
      fw: S.anRange === id ? '600' : '500',
    })),
    // KPI band
    anNet: sgn(net),
    anNetFg: net >= 0 ? 'var(--pos)' : 'var(--neg)',
    anCount: String(T.length),
    anWinPct: winRate.toFixed(2) + '%',
    anWinTicks: gaugeTicks(winRate, 'var(--pos)'),
    anWinSub: wins.length + '/' + losses.length,
    anPf: pf ? pf.toFixed(2) : '—',
    anPfFg: pf >= 1 ? 'var(--pos)' : 'var(--neg)',
    anPfTicks: gaugeTicks((Math.min(pf, 3) / 3) * 100, pf >= 1 ? 'var(--pos)' : 'var(--neg)'),
    anDayPct: dayWin.toFixed(2) + '%',
    anDayTicks: gaugeTicks(dayWin, 'var(--pos)'),
    anDaySub: winDays + '/' + loseDays,
    anRatio: ratio.toFixed(2),
    anRatioW: (avgWin ? (avgWin / (avgWin + avgLoss)) * 100 : 50).toFixed(1) + '%',
    anAvgWin: sgn(avgWin),
    anAvgLoss: sgn(-avgLoss),
    anStreak: String(bestWs),
    anStreakSub: worstLs + ' worst losing run',
    // overview
    anCumPath: cPath,
    anCumFill: cPath ? cPath + ' L420,' + zeroY + ' L0,' + zeroY + ' Z' : '',
    anCumZero: zeroY,
    anCumFg: net >= 0 ? 'var(--pos)' : 'var(--neg)',
    anHasCum: cum.length > 1,
    anDayBars: dayKeys
      .slice(0, 14)
      .reverse()
      .map((k) => {
        const peak = Math.max(1, ...dayKeys.map((x) => Math.abs(dayPnl(x))));
        return {
          label: dayLabel(k),
          h: ((Math.abs(dayPnl(k)) / peak) * 100).toFixed(1) + '%',
          pos: dayPnl(k) >= 0,
          neg: dayPnl(k) < 0,
        };
      }),
    anPerfStats: [
      { k: 'Total trades', v: String(T.length) },
      { k: 'Winners / losers', v: wins.length + ' / ' + losses.length },
      { k: 'Win rate', v: winRate.toFixed(2) + '%' },
      { k: 'Expectancy', v: sgn(expectancy) },
      { k: 'Profit factor', v: pf ? pf.toFixed(2) : '—' },
      { k: 'Avg win', v: sgn(avgWin) },
      { k: 'Avg loss', v: sgn(-avgLoss) },
      { k: 'Avg win / loss', v: ratio.toFixed(2) },
      { k: 'Gross profit', v: sgn(grossWin) },
      { k: 'Gross loss', v: sgn(-grossLoss) },
      { k: 'Best win streak', v: String(bestWs) },
      { k: 'Worst losing streak', v: String(worstLs) },
      { k: 'Trading days', v: String(dayKeys.length) },
      { k: 'Winning days', v: winDays + ' of ' + dayKeys.length },
    ],
    anCal: cal,
    anSessionCards: (() => {
      const DEF: [string, string, string, string][] = [
        ['Asia', 'ph-moon-stars', 'var(--vio-fill)', 'var(--vio)'],
        ['London', 'ph-clock', 'var(--sky-fill)', 'var(--sky)'],
        ['London → NY overlap', 'ph-clock-clockwise', 'var(--amb)', 'var(--amb-2)'],
        ['New York', 'ph-clock', 'var(--pos)', 'var(--pos-2)'],
      ];
      const m: Record<string, Agg> = {};
      T.forEach((t) => {
        const k = sessionOf(t);
        const e = (m[k] = m[k] || { n: 0, pnl: 0, w: 0 });
        e.n++;
        e.pnl += t.pnl;
        if (t.pnl > 0) e.w++;
      });
      const peak = Math.max(1, ...Object.keys(m).map((k) => Math.abs((m[k] as Agg).pnl)));
      const total = T.length || 1;
      return DEF.map(([label, icon, dot, ink]) => {
        const e = m[label] || { n: 0, pnl: 0, w: 0 };
        return {
          label,
          icon,
          dot,
          ink,
          share: Math.round((e.n / total) * 100) + '% of trades',
          pnl: sgn(e.pnl),
          pnlFg: e.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
          avg: e.n ? sgn(e.pnl / e.n) + ' / trade' : '+$0.00 / trade',
          trades: String(e.n),
          wr: e.n ? ((e.w / e.n) * 100).toFixed(0) + '%' : '--%',
          wl: e.n ? e.w + '/' + e.n : '0/0',
          w: ((Math.abs(e.pnl) / peak) * 100).toFixed(1) + '%',
        };
      });
    })(),
    anBestSession: (() => {
      const m: Record<string, number> = {};
      T.forEach((t) => {
        const k = sessionOf(t);
        m[k] = (m[k] || 0) + t.pnl;
      });
      const keys = Object.keys(m);
      // reference returns null; the generated ViewModel types this top-level object as non-nullable and the view
      // guards it with a truthiness check, so undefined renders identically
      if (!keys.length) return undefined;
      const best = keys.sort((a, b) => (m[b] as number) - (m[a] as number))[0] as string;
      return { label: best, pnl: sgn(m[best] as number) };
    })(),
    anHourStrip: (() => {
      const SESS_DOT: Record<string, string> = {
        Asia: 'var(--vio-fill)',
        London: 'var(--sky-fill)',
        'London → NY overlap': 'var(--amb)',
        'New York': 'var(--pos)',
      };
      // fixed per-session ink — chosen against each fill's own lightness, not the app theme
      const SESS_INK: Record<string, string> = {
        Asia: '#000000', // token-lint-allow
        London: '#000000', // token-lint-allow
        'London → NY overlap': '#f4f1e8', // token-lint-allow
        'New York': '#f4f1e8', // token-lint-allow
      };
      const m: Record<string, { n: number; sess: Record<string, number> }> = {};
      T.forEach((t) => {
        const hh = String(t.time).slice(0, 2);
        const e = (m[hh] = m[hh] || { n: 0, sess: {} });
        e.n++;
        const s = sessionOf(t);
        e.sess[s] = (e.sess[s] || 0) + 1;
      });
      const peak = Math.max(1, ...Object.keys(m).map((k) => (m[k] as { n: number }).n));
      const HOURS = [
        '00',
        '01',
        '02',
        '03',
        '04',
        '05',
        '06',
        '07',
        '08',
        '09',
        '10',
        '11',
        '12',
        '13',
        '14',
        '15',
        '16',
        '17',
        '18',
        '19',
        '20',
        '21',
        '22',
        '23',
      ];
      return HOURS.map((hh) => {
        const e = m[hh];
        const dom = e
          ? Object.keys(e.sess).sort((a, b) => (e.sess[b] as number) - (e.sess[a] as number))[0]
          : null;
        return {
          h: hh,
          showLabel: ['00', '03', '06', '09', '12', '15', '18', '21'].indexOf(hh) >= 0,
          has: !!e,
          n: e ? String(e.n) : '',
          hpx: e ? Math.max(18, Math.round((e.n / peak) * 60)) + 'px' : '4px',
          dot: dom ? SESS_DOT[dom] : 'var(--l10)',
          ink: dom ? SESS_INK[dom] : 'var(--text)',
        };
      });
    })(),
    anSymbols: group((t) => t.sym),
    anWeekdays: group((t) => weekday(t.off)),
    anStrats: group((t) => t.strat),
    anLogAcctChips: D.logAccounts.map((a) => {
      const on = S.anLogAcct.indexOf(a.id) >= 0;
      return {
        id: a.id,
        label: a.nickname.split(' ')[0] as string,
        on,
        bd: on ? 'color-mix(in srgb, ' + a.colour + ' 50%, transparent)' : 'var(--l10)',
        bg: on ? 'color-mix(in srgb, ' + a.colour + ' 12%, transparent)' : 'transparent',
        fg: on ? a.ink : 'var(--muted)',
        go: () =>
          c.set((s: ModelState) => {
            const has = s.anLogAcct.indexOf(a.id) >= 0;
            const next = has ? s.anLogAcct.filter((x) => x !== a.id) : s.anLogAcct.concat(a.id);
            return { anLogAcct: next.length ? next : [a.id] };
          }),
      };
    }),
    anLog: ordered
      .filter((t) => S.anLogAcct.indexOf(t.acct) >= 0)
      .slice(0, 20)
      .map((t, i) => ({
        rowBg: i % 2 === 1 ? 'var(--l3)' : 'transparent',
        date: dayLabel(t.off),
        sym: t.sym,
        side: t.side,
        acct: t.acct,
        lots: t.lots.toFixed(2),
        entry: t.entry,
        exit: t.exit,
        pnl: sgn(t.pnl),
        pnlFg: t.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
        strat: t.strat,
        sideFg: t.side === 'BUY' ? 'var(--pos)' : 'var(--neg)',
        sideBg:
          t.side === 'BUY'
            ? 'color-mix(in srgb, var(--pos) 12%, transparent)'
            : 'color-mix(in srgb, var(--neg-fill) 14%, transparent)',
      })),
    anLogCount: (() => {
      const n = ordered.filter((t) => S.anLogAcct.indexOf(t.acct) >= 0).length;
      return n === 1 ? '1 trade' : n + ' trades';
    })(),
    anIsTech: S.anTab === 'technicals',
    anIsBehaviour: S.anTab === 'behaviour',
    // hour-of-day histogram
    anHours: (() => {
      const m: Record<string, { n: number; pnl: number }> = {};
      T.forEach((t) => {
        const hh = String(t.time).slice(0, 2);
        const e = (m[hh] = m[hh] || { n: 0, pnl: 0 });
        e.n++;
        e.pnl += t.pnl;
      });
      const keys = Object.keys(m).sort();
      const peak = Math.max(1, ...keys.map((k) => Math.abs((m[k] as { pnl: number }).pnl)));
      return keys.map((k) => {
        const e = m[k] as { n: number; pnl: number };
        return {
          h: k,
          count: String(e.n),
          pnl: sgn(e.pnl),
          pnlFg: e.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
          height: ((Math.abs(e.pnl) / peak) * 100).toFixed(1) + '%',
          pos: e.pnl >= 0,
          neg: e.pnl < 0,
        };
      });
    })(),
    // win % by day of week
    anWinByDow: (() => {
      const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
      const m: Record<string, { n: number; w: number }> = {};
      T.forEach((t) => {
        const d = weekday(t.off);
        const e = (m[d] = m[d] || { n: 0, w: 0 });
        e.n++;
        if (t.pnl > 0) e.w++;
      });
      return DOW.map((d) => {
        const e = m[d] || { n: 0, w: 0 };
        const wr = e.n ? (e.w / e.n) * 100 : 0;
        return {
          d,
          n: e.n,
          wr: e.n ? wr.toFixed(0) + '%' : '—',
          h: e.n ? Math.max(4, wr).toFixed(1) + '%' : '2%',
          bar: !e.n ? 'var(--l8)' : wr >= 50 ? 'var(--pos)' : 'var(--neg)',
        };
      });
    })(),
    // entry hour × weekday scatter — one dot per trade
    anEntryDayScatter: (() => {
      const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
      const pts = T.filter((t) => DOW.indexOf(weekday(t.off)) >= 0).map((t) => {
        const hh = hh2(t),
          mm = mm2(t);
        const row = DOW.indexOf(weekday(t.off));
        return {
          cx: (((hh + mm / 60) / 24) * 400 + 10).toFixed(1),
          cy: ((row / 4) * 108 + 10).toFixed(1),
          fill: t.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
          r: Math.min(7, 3 + Math.abs(t.pnl) / 300).toFixed(1),
          title: t.sym + ' · ' + weekday(t.off) + ' ' + t.time + ' · ' + sgn(t.pnl),
        };
      });
      return pts;
    })(),
    anEntryDayRows: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map((d, i) => ({
      d,
      y: ((i / 4) * 108 + 10).toFixed(1),
    })),
    // trade duration vs entry hour scatter
    anDurationScatter: (() => {
      const durs = T.map((t) => t.durS / 60);
      const peakDur = Math.max(30, ...durs);
      return T.map((t) => {
        const hh = hh2(t),
          mm = mm2(t);
        const durMin = t.durS / 60;
        return {
          cx: (((hh + mm / 60) / 24) * 400 + 10).toFixed(1),
          cy: (128 - Math.min(1, durMin / peakDur) * 118).toFixed(1),
          fill: t.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
          title:
            t.sym +
            ' · ' +
            t.time +
            ' · ' +
            (durMin < 60 ? durMin.toFixed(0) + 'm' : (durMin / 60).toFixed(1) + 'h') +
            ' · ' +
            sgn(t.pnl),
        };
      });
    })(),
    anDurationPeak: (() => {
      const durs = T.map((t) => t.durS / 60);
      const p = Math.max(30, ...durs);
      return p < 60 ? p.toFixed(0) + 'm' : (p / 60).toFixed(1) + 'h';
    })(),
    // time-of-day profitability heat map — 4h buckets × weekday
    anHourHeat: (() => {
      const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
      const BUCKETS: [string, string][] = [
        ['00', '04'],
        ['04', '08'],
        ['08', '12'],
        ['12', '16'],
        ['16', '20'],
        ['20', '24'],
      ];
      const m: Record<string, Agg> = {};
      T.forEach((t) => {
        const d = weekday(t.off);
        if (DOW.indexOf(d) < 0) return;
        const hh = hh2(t);
        const bi = Math.min(5, Math.floor(hh / 4));
        const k = bi + '|' + d;
        const e = (m[k] = m[k] || { n: 0, pnl: 0, w: 0 });
        e.n++;
        e.pnl += t.pnl;
        if (t.pnl > 0) e.w++;
      });
      const peak = Math.max(1, ...Object.keys(m).map((k) => Math.abs((m[k] as Agg).pnl)));
      return BUCKETS.map((b, bi) => ({
        label: b[0] + '–' + b[1],
        cells: DOW.map((d) => {
          const e = m[bi + '|' + d];
          const inten = e ? Math.min(Math.abs(e.pnl) / peak, 1) : 0;
          return {
            has: !!e,
            bg: !e
              ? 'var(--l4)'
              : e.pnl >= 0
                ? 'color-mix(in srgb, var(--pos) ' + Math.round(10 + inten * 50) + '%, transparent)'
                : 'color-mix(in srgb, var(--neg) ' + Math.round(10 + inten * 50) + '%, transparent)',
            pnl: e ? sgn(e.pnl) : '—',
            pnlFg: e ? (e.pnl >= 0 ? 'var(--pos)' : 'var(--neg)') : 'var(--faint)',
            title: e
              ? (e.n === 1 ? '1 trade' : e.n + ' trades') +
                ' · ' +
                ((e.w / e.n) * 100).toFixed(0) +
                '% win rate · ' +
                sgn(e.pnl)
              : 'No trades',
          };
        }),
      }));
    })(),
    anHourHeatDows: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
    // drawdown from running peak
    anDdPath: (() => {
      let run = 0,
        peak = 0;
      const dd = dayKeys
        .slice()
        .sort((a, b) => b - a)
        .map((k) => {
          run += dayPnl(k);
          peak = Math.max(peak, run);
          return peak - run;
        });
      if (dd.length < 2) return '';
      const worst = Math.max(1, ...dd);
      return (
        'M' +
        dd
          .map((v, i) => ((i / (dd.length - 1)) * 420).toFixed(1) + ',' + ((v / worst) * 92).toFixed(1))
          .join(' L')
      );
    })(),
    anDdWorst: (() => {
      let run = 0,
        peak = 0,
        worst = 0;
      dayKeys
        .slice()
        .sort((a, b) => b - a)
        .forEach((k) => {
          run += dayPnl(k);
          peak = Math.max(peak, run);
          worst = Math.max(worst, peak - run);
        });
      return worst ? '−$' + worst.toFixed(2) : '$0.00';
    })(),
    // account balance overlay — starting capital is a fixed baseline, balance = baseline + cumulative P&L
    anBalPath: (() => {
      const BASE = D.baseCapital;
      const bal = cum.map((p) => BASE + p.run);
      if (bal.length < 2) return '';
      const lo = Math.min(...bal),
        hi = Math.max(BASE + 1, ...bal);
      return (
        'M' +
        bal
          .map(
            (v, i) =>
              ((i / (bal.length - 1)) * 420).toFixed(1) +
              ',' +
              (150 - ((v - lo) / (hi - lo)) * 138).toFixed(1),
          )
          .join(' L')
      );
    })(),
    anBalStart: '$500,000.00',
    anBalEnd:
      '$' +
      (D.baseCapital + net).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    // drawdown schedule — distinct peak→trough episodes, deepest first
    anDdEpisodes: ddEpisodesRaw.map((e) => ({
      range: dayLabelPre(e.peakOff) + ' → ' + dayLabelPre(e.troughOff),
      depth: '−$' + e.depth.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
      status: e.recovered ? 'Recovered ' + dayLabelPre(e.endOff as number) : 'Ongoing',
      statusFg: e.recovered ? 'var(--pos)' : 'var(--amb-2)',
    })),
    anDdHasEpisodes: ddEpisodesRaw.length > 0,
    anDdNoEpisodes: ddEpisodesRaw.length === 0,
    // previous-day and periodic comparisons (respect strategy/symbol chips, ignore the range selector)
    anCmp: (() => {
      const TF = all.filter(
        (t) => (S.anStrat === 'all' || t.strat === S.anStrat) && (S.anSym === 'all' || t.sym === S.anSym),
      );
      const sumR = (lo: number, hi: number) =>
        TF.filter((t) => t.off >= lo && t.off < hi).reduce((a, t) => a + t.pnl, 0);
      const cntR = (lo: number, hi: number) => TF.filter((t) => t.off >= lo && t.off < hi).length;
      const wrR = (lo: number, hi: number): number | null => {
        const arr = TF.filter((t) => t.off >= lo && t.off < hi);
        const w = arr.filter((t) => t.pnl > 0).length;
        return arr.length ? (w / arr.length) * 100 : null;
      };
      const mk = (label: string, lo: number, hi: number, loP: number, hiP: number) => {
        const curV = sumR(lo, hi),
          prevV = sumR(loP, hiP);
        const delta = curV - prevV;
        const pct = prevV !== 0 ? (delta / Math.abs(prevV)) * 100 : curV !== 0 ? (curV > 0 ? 100 : -100) : 0;
        return {
          label,
          cur: sgn(curV),
          curFg: curV >= 0 ? 'var(--pos)' : 'var(--neg)',
          prev: sgn(prevV),
          curN: cntR(lo, hi),
          prevN: cntR(loP, hiP),
          curWr: (() => {
            const w = wrR(lo, hi);
            return w === null ? '—' : w.toFixed(0) + '%';
          })(),
          deltaLabel:
            (delta >= 0 ? '▲ ' : '▼ ') +
            (delta >= 0 ? '+' : '−') +
            '$' +
            Math.abs(delta).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) +
            ' (' +
            (pct >= 0 ? '+' : '−') +
            Math.abs(pct).toFixed(0) +
            '%)',
          deltaFg: delta >= 0 ? 'var(--pos)' : 'var(--neg)',
        };
      };
      return [
        mk('Today vs. yesterday', 0, 1, 1, 2),
        mk('This week vs. last week', 0, 7, 7, 14),
        mk('This month vs. last month', 0, 30, 30, 60),
      ];
    })(),
    // calendar footer: weekday totals
    anWeekTotals: (() => {
      const m: Record<string, number> = {};
      T.forEach((t) => {
        const d = weekday(t.off);
        m[d] = (m[d] || 0) + t.pnl;
      });
      const order = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
      const peak = Math.max(1, ...order.map((d) => Math.abs(m[d] || 0)));
      return order.map((d) => {
        const v = m[d];
        return {
          d,
          pnl: v === undefined ? '—' : sgn(v),
          pnlFg: (v || 0) >= 0 ? 'var(--pos)' : 'var(--neg)',
          w: ((Math.abs(v || 0) / peak) * 100).toFixed(1) + '%',
          bar: (v || 0) >= 0 ? 'var(--pos)' : 'var(--neg)',
        };
      });
    })(),
    // per-strategy expectancy
    anStratExp: (() => {
      const m: Record<string, { n: number; pnl: number; w: number; gw: number; gl: number }> = {};
      T.forEach((t) => {
        const e = (m[t.strat] = m[t.strat] || { n: 0, pnl: 0, w: 0, gw: 0, gl: 0 });
        e.n++;
        e.pnl += t.pnl;
        if (t.pnl > 0) {
          e.w++;
          e.gw += t.pnl;
        } else e.gl += Math.abs(t.pnl);
      });
      const at = (k: string) => m[k] as { n: number; pnl: number; w: number; gw: number; gl: number };
      return Object.keys(m)
        .sort((a, b) => at(b).pnl / at(b).n - at(a).pnl / at(a).n)
        .map((k) => {
          const e = at(k);
          return {
            label: k,
            count: e.n === 1 ? '1 trade' : e.n + ' trades',
            wr: ((e.w / e.n) * 100).toFixed(0) + '%',
            exp: sgn(e.pnl / e.n),
            expFg: e.pnl / e.n >= 0 ? 'var(--pos)' : 'var(--neg)',
            pf: e.gl ? (e.gw / e.gl).toFixed(2) : e.gw ? '∞' : '—',
            pnl: sgn(e.pnl),
            pnlFg: e.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
          };
        });
    })(),
    // ── Technicals ──
    anDirection: (() => {
      const mk = (side: string) => {
        const g = T.filter((t) => t.side === side);
        const w = g.filter((t) => t.pnl > 0).length;
        const pnl = g.reduce((a, t) => a + t.pnl, 0);
        return {
          label: side === 'BUY' ? 'Long' : 'Short',
          n: g.length,
          w,
          pnl,
          count: g.length === 1 ? '1 trade' : g.length + ' trades',
          wr: g.length ? ((w / g.length) * 100).toFixed(0) + '%' : '—',
          pnlStr: sgn(pnl),
          pnlFg: pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
        };
      };
      const a = mk('BUY'),
        b = mk('SELL');
      const peak = Math.max(1, Math.abs(a.pnl), Math.abs(b.pnl));
      return [a, b].map((x) => ({
        ...x,
        w2: ((Math.abs(x.pnl) / peak) * 100).toFixed(1) + '%',
        bar: x.pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
      }));
    })(),
    anClasses: group((t) => D.symbolClasses[t.sym] || 'FX'),
    anHold: (() => {
      // data-lint-allow: hold-time bucket boundaries the reference bins by (presentation binning, §9.4)
      const B: [string, number, number][] = [
        ['Under 5m', 0, 300],
        ['5–30m', 300, 1800],
        ['30m–2h', 1800, 7200],
        ['Over 2h', 7200, 1e9],
      ];
      const peak = Math.max(
        1,
        ...B.map(([, lo, hi]) =>
          Math.abs(T.filter((t) => t.durS >= lo && t.durS < hi).reduce((a, t) => a + t.pnl, 0)),
        ),
      );
      return B.map(([label, lo, hi]) => {
        const g = T.filter((t) => t.durS >= lo && t.durS < hi);
        const pnl = g.reduce((a, t) => a + t.pnl, 0);
        return {
          label,
          count: g.length === 1 ? '1 trade' : g.length + ' trades',
          wr: g.length ? ((g.filter((t) => t.pnl > 0).length / g.length) * 100).toFixed(0) + '%' : '—',
          pnl: g.length ? sgn(pnl) : '—',
          pnlFg: pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
          w: ((Math.abs(pnl) / peak) * 100).toFixed(1) + '%',
          bar: pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
        };
      });
    })(),
    anRDist: (() => {
      const unit = avgLoss || 1;
      // data-lint-allow: R-distribution bucket boundaries (presentation binning, §9.4)
      const B: [string, number, number][] = [
        ['≤ −2R', -99, -2],
        ['−2 to −1R', -2, -1],
        ['−1 to 0R', -1, 0],
        ['0 to +1R', 0, 1],
        ['+1 to +2R', 1, 2],
        ['≥ +2R', 2, 99],
      ];
      const counts = B.map(([label, lo, hi]) => ({
        label,
        n: T.filter((t) => {
          const r = t.pnl / unit;
          return r >= lo && r < hi;
        }).length,
      }));
      const peak = Math.max(1, ...counts.map((x) => x.n));
      return counts.map((x) => ({
        ...x,
        h: ((x.n / peak) * 100).toFixed(1) + '%',
        count: String(x.n),
        bar: /−/.test(x.label) || /≤/.test(x.label) ? 'var(--neg)' : 'var(--pos)',
      }));
    })(),
    anRUnit: 'R measured against your average loss of ' + sgn(-avgLoss),
    anLotBands: (() => {
      // data-lint-allow: lot-size band boundaries (presentation binning, §9.4)
      const B: [string, number, number][] = [
        ['≤ 0.05', 0, 0.051],
        ['0.06–0.10', 0.051, 0.101],
        ['0.11–0.20', 0.101, 0.201],
        ['Over 0.20', 0.201, 99],
      ];
      return B.map(([label, lo, hi]) => {
        const g = T.filter((t) => t.lots >= lo && t.lots < hi);
        const pnl = g.reduce((a, t) => a + t.pnl, 0);
        return {
          label,
          count: g.length + (g.length === 1 ? ' trade' : ' trades'),
          pnl: g.length ? sgn(pnl) : '—',
          pnlFg: pnl >= 0 ? 'var(--pos)' : 'var(--neg)',
          wr: g.length ? ((g.filter((t) => t.pnl > 0).length / g.length) * 100).toFixed(0) + '%' : '—',
        };
      });
    })(),
    // ── Behaviour ──
    anBehaviour: (() => {
      const ARMED = D.armedStrategies;
      const off = T.filter((t) => ARMED.indexOf(t.strat) < 0);
      const offPnl = off.reduce((a, t) => a + t.pnl, 0);
      const onPnl = net - offPnl;
      const onN = T.length - off.length;
      const journalled = D.journalDayKeys.length;
      const asia = T.filter((t) => hh2(t) < 7);
      return [
        {
          k: 'Plan adherence',
          v: T.length ? ((onN / T.length) * 100).toFixed(0) + '%' : '—',
          sub: onN + ' of ' + T.length + ' entries on an armed strategy',
          c: onN === T.length ? 'var(--pos)' : 'var(--amb-2)',
        },
        {
          k: 'Off-plan P&L',
          v: off.length ? sgn(offPnl) : '$0.00',
          sub:
            off.length + (off.length === 1 ? ' entry outside the playbook' : ' entries outside the playbook'),
          c: offPnl >= 0 ? 'var(--pos)' : 'var(--neg)',
        },
        {
          k: 'On-plan P&L',
          v: sgn(onPnl),
          sub: 'expectancy ' + (onN ? sgn(onPnl / onN) : '—') + ' per entry',
          c: onPnl >= 0 ? 'var(--pos)' : 'var(--neg)',
        },
        {
          k: 'Out-of-window entries',
          v: String(asia.length),
          sub: 'taken before 07:00 London',
          c: asia.length ? 'var(--amb-2)' : 'var(--pos)',
        },
        {
          k: 'Journalled days',
          v: String(journalled),
          sub: 'sessions carrying a written review',
          c: 'var(--sky)',
        },
        {
          k: 'Worst losing run',
          v: String(worstLs),
          sub: 'consecutive losses in this range',
          c: worstLs > 2 ? 'var(--neg)' : 'var(--text)',
        },
      ];
    })(),
    anBehaviourStrats: group(
      (t) => (D.armedStrategies.indexOf(t.strat) >= 0 ? 'On plan · ' : 'Off plan · ') + t.strat,
    ),
    anHeatRanges: (
      [
        ['month', 'Month'],
        ['quarter', 'Quarter'],
        ['year', 'Year'],
        ['all', 'All Time'],
      ] as [string, string][]
    ).map(([id, label]) => ({
      label,
      go: () => c.set({ anHeatRange: id }),
      bg: S.anHeatRange === id ? 'var(--rail)' : 'transparent',
      fg: S.anHeatRange === id ? 'var(--rail-ink)' : 'var(--muted)',
      fw: S.anHeatRange === id ? '600' : '500',
    })),
    anHeatRows: (() => {
      const HT = all.filter((t) => t.off <= heatWindow);
      const S_ORDER = ['Asia', 'London', 'London → NY overlap', 'New York'].filter((s) =>
        HT.some((t) => sessionOf(t) === s),
      );
      const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
      const m: Record<string, Agg> = {};
      HT.forEach((t) => {
        const k = sessionOf(t) + '|' + weekday(t.off);
        const e = (m[k] = m[k] || { n: 0, pnl: 0, w: 0 });
        e.n++;
        e.pnl += t.pnl;
        if (t.pnl > 0) e.w++;
      });
      const peak = Math.max(1, ...Object.keys(m).map((k) => Math.abs((m[k] as Agg).pnl)));
      return S_ORDER.map((s) => ({
        label: s,
        cells: DAYS.map((d) => {
          const e = m[s + '|' + d];
          return e
            ? {
                has: true,
                n: String(e.n),
                pnl: sgn(e.pnl),
                wr: ((e.w / e.n) * 100).toFixed(0) + '%',
                bg:
                  e.pnl >= 0
                    ? 'color-mix(in srgb, var(--pos) ' +
                      Math.round(8 + (Math.abs(e.pnl) / peak) * 18) +
                      '%, transparent)'
                    : 'color-mix(in srgb, var(--neg) ' +
                      Math.round(8 + (Math.abs(e.pnl) / peak) * 18) +
                      '%, transparent)',
                fg: e.pnl >= 0 ? 'var(--pos-2)' : 'var(--neg)',
              }
            : { has: false };
        }),
      }));
    })(),
    anHeatEmpty: all.filter((t) => t.off <= heatWindow).length === 0,
    anHeatHasData: all.filter((t) => t.off <= heatWindow).length > 0,
    anHeatDays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
    anEmpty: T.length === 0,
  } satisfies Partial<ViewModel>;
}
export type AnalyticsModel = ReturnType<typeof buildAnalytics>;
